import { addMinutes, format, parse } from 'date-fns';
import { FALLBACK_OPEN_TIME } from '@/utils/openingHours';

/** Workshop default when `defaultPickupBufferMinutes` has never been set (#2117). */
export const DEFAULT_PICKUP_BUFFER_MINUTES = 60;

const DATETIME_LOCAL = "yyyy-MM-dd'T'HH:mm";
const SLOT_MINUTES = 15;

/** Duration is known only when it is a positive finite number of hours (#2335). */
export function hasKnownDuration(durationHours: number): boolean {
  return Number.isFinite(durationHours) && durationHours > 0;
}

function resolveBufferMinutes(bufferMinutes: number | null | undefined): number {
  return bufferMinutes == null ? DEFAULT_PICKUP_BUFFER_MINUTES : bufferMinutes;
}

function parseDateTimeLocal(value: string): Date | null {
  if (!value) return null;
  const date = parse(value, DATETIME_LOCAL, new Date());
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Pickup suggestion = arrival + selected-work duration + workshop buffer,
 * rounded up to the next 15-minute civil slot.
 *
 * Returns null when duration is unknown or zero: a silent buffer-only
 * suggestion would look like a real estimate (#2335).
 *
 * Uses the same `yyyy-MM-dd'T'HH:mm` parse/format as SchedulingStep — no
 * timezone conversion, no UTC-midnight special case.
 */
export function estimatePickupAt(input: {
  arrivalAtStr: string;
  durationHours: number;
  bufferMinutes?: number | null;
}): string | null {
  if (!hasKnownDuration(input.durationHours)) return null;
  const arrival = parseDateTimeLocal(input.arrivalAtStr);
  if (arrival == null) return null;

  const durationMinutes = Math.round(input.durationHours * 60);
  const raw = addMinutes(arrival, durationMinutes + resolveBufferMinutes(input.bufferMinutes));
  return format(roundUpToNextQuarterHour(raw), DATETIME_LOCAL);
}

/**
 * Pickup field value while the user has not edited it.
 *
 * Exact: arrival + duration + buffer, or null when duration is unknown.
 * Floating: the arrival is a midnight day marker, not a clock time, so the
 * estimate starts from that day's opening time (`openTime`, 08:00 when the
 * workshop hours are unknown), or from a later clock the arrival carries
 * (a calendar slot) — never from midnight (#4095). With unknown
 * duration there is nothing to estimate, so the field is cleared ('') rather
 * than showing 0.00 or a buffer-only guess (#2335).
 */
export function suggestedPickupAt(input: {
  schedulingType: 'exact' | 'floating';
  arrivalAtStr: string;
  durationHours: number;
  bufferMinutes?: number | null;
  openTime?: string | null;
}): string | null {
  if (input.schedulingType !== 'exact') {
    if (!input.arrivalAtStr) return null;
    if (!hasKnownDuration(input.durationHours)) return '';
    const [day = '', clock = ''] = input.arrivalAtStr.split('T');
    const openTime = input.openTime || FALLBACK_OPEN_TIME;
    // A clock carried over from a calendar slot counts once the workshop is
    // open; midnight (the day marker) and earlier times start at opening.
    // Both are zero-padded HH:mm, so string order is time order.
    const start = clock > openTime ? clock : openTime;
    return estimatePickupAt({
      arrivalAtStr: `${day}T${start}`,
      durationHours: input.durationHours,
      bufferMinutes: input.bufferMinutes ?? null,
    });
  }
  return estimatePickupAt(input);
}

/**
 * Whether requested pickup leaves enough room for the reserved duration plus
 * the workshop buffer: `pickup − arrival >= duration + buffer` (#2335).
 *
 * Unknown/zero duration and unparseable timestamps are treated as fitting —
 * those cases have their own messaging, and a missing clock is not a squeeze.
 * The caller decides whether to skip this check (floating midnight is not a
 * clock time).
 */
export function pickupFitsWorkWindow(input: {
  arrivalAtStr: string;
  pickupAtStr: string;
  durationHours: number;
  bufferMinutes?: number | null;
}): boolean {
  if (!hasKnownDuration(input.durationHours)) return true;
  const arrival = parseDateTimeLocal(input.arrivalAtStr);
  const pickup = parseDateTimeLocal(input.pickupAtStr);
  if (arrival == null || pickup == null) return true;

  const requiredMinutes =
    Math.round(input.durationHours * 60) + resolveBufferMinutes(input.bufferMinutes);
  const availableMinutes = (pickup.getTime() - arrival.getTime()) / 60_000;
  return availableMinutes >= requiredMinutes;
}

function roundUpToNextQuarterHour(date: Date): Date {
  const leftover = date.getSeconds() > 0 || date.getMilliseconds() > 0;
  const totalMinutes = date.getHours() * 60 + date.getMinutes() + (leftover ? 1 : 0);
  const roundedMinutes = Math.ceil(totalMinutes / SLOT_MINUTES) * SLOT_MINUTES;
  const startOfCivilDay = parse(`${format(date, 'yyyy-MM-dd')}T00:00`, DATETIME_LOCAL, new Date());
  return addMinutes(startOfCivilDay, roundedMinutes);
}
